import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Typography, Checkbox, Skeleton, Dialog, IconButton } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { INTERVIEW_TYPE, MockPublishingSteps, TemplateType } from 'components/PersonalizedInterview/constant';
import TextField, { SearchTextField } from 'components/CustomFields';
import SelectDatePicker, { SimpleSelect } from 'components/CustomSelectFields';
import CustomButton from 'components/CustomButtons';
import MockDatePicker from 'components/PersonalizedInterview/MockDatePicker';
import ProctorSettings from 'components/PersonalizedInterview/MockProctorSettings';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  createChallenge, getChallengesByTags, getCompaniesWithDetails, getSubTopicsWithDetails
} from 'services/codeChallengeService';
import { useServices } from 'contexts/SingleSessionProvider';
import { useSnackbar } from 'contexts';
import { getBatches } from 'services';
import { Session } from 'utils';
import CloseIcon from '@mui/icons-material/Close';
import InfoIconWithTooltip from 'components/InfoIconWithTooltip';

const useStyles = makeStyles((theme) => ({
  container: {
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    display: 'flex',
    flexDirection : 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  box: {
    padding: theme.spacing(10, 15),
    display: 'flex',
    gap: theme.spacing(2.5),
    flexDirection: 'column',
    width: '700px',
    background: '#FFFFFF',
    borderRadius: theme.spacing(2),
    boxShadow: `0px 4px 4px 0px #CCD4DE1A,
                0px 10px 20px 0px #CCD4DE4D,
                0px 7px 28px 0px #CCD4DE99`,
  },
  hoverButton: {
    marginRight : '16px',
    '&:hover': {
      cursor: 'pointer',
      // boxShadow: '0px 0px 4px 0px #00000026',
      // borderRadius: 100,
    },
  },
  borderBottom: {
    borderBottom: `1px solid ${theme.palette.neutral['clr-400']}`,
    paddingBottom: theme.spacing(5),
  },
  headBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  selectInterview: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(5),
    width: '100%',
  },
  interviewBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(5, 4),
    backgroundColor: theme.palette.primary['clr-50'],
    borderRadius: theme.spacing(1),
    border : `1px solid ${theme.palette.primary['clr-50']}`,
    cursor: 'pointer',
    '&:hover': {
      cursor: 'pointer',
      border : `1px solid ${theme.palette.primary.main}`,
      boxShadow: `0px 4px 4px 0px #CCD4DE1A,
                    0px 8px 20px 0px #CCD4DE1A`

    },
  },
  stepsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '100%',
  },
  stepNavigation: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(5),
  },
  selectCompany : {
    display : 'flex',
    flexDirection : 'column',
    gap : theme.spacing(4),
    width : '100%',
    marginTop : theme.spacing(3.5)
  },
  companies : {
    display : 'flex',
    flexWrap : 'wrap',
    gap : theme.spacing(2),
    overflow : 'auto',
    maxHeight  :'256px'
  },
  companyCard : {
    maxWidth : '278px',
    width : '100%',
    height : '80px',
    border : `1px solid ${theme.palette.primary['clr-50']}`,
    backgroundColor : theme.palette.shades['clr-offWhite'],
    display : 'flex',
    gap : theme.spacing(4),
    alignItems : 'center',
    borderRadius : theme.spacing(1),
    padding : theme.spacing(4),
    '&:hover' : {
        boxShadow: `0px 4px 4px 0px #CCD4DE1A;
                    0px 8px 20px 0px #CCD4DE1A`,
        border : `1px solid ${theme.palette.primary['clr-300']}`,
        backgroundColor : theme.palette.neutral['clr-50'],
        cursor : 'pointer'
    }
  },
  cardImgContainer: {
    width: '56px', height: '56px',
    borderRadius: '8px',
    border: '1px solid #CCD4DE',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor : '#FFFFFF'
  },
}));

export const Header = ({ onBack, currentStep }) => {
  const classes = useStyles();
  return (
    <Box className={`${classes.borderBottom} ${classes.headBox}`}>
      {currentStep && (
        <ArrowBack
          onClick={onBack}
          className={classes.hoverButton}
          sx={{ color: '#1961BF' }}
        />
      )}
     
      <Typography variant="h4-medium">
        {currentStep ? `${currentStep}` : "Create a new mock"}
      </Typography>
    </Box>
  );
};

const InterviewSelection = ({ onSelectDSA, onSelectCompany }) => {
  const classes = useStyles();

  const interviewOptions = [
    {
      id: 'dsa',
      title: 'General DSA Challenges',
      description: 'Challenges are curated by subject matter experts',
      imageSrc: 'https://assets.languify.in/images/codeTag.png',
      onClick: onSelectDSA,
    },
    {
      id: 'company',
      title: 'Company specific Challenges',
      description: 'Challenges are based on past company interviews',
      imageSrc: 'https://assets.languify.in/images/companyTag.png',
      onClick: onSelectCompany,
    },
  ];

  return (
    <Box className={classes.selectInterview}>
      {interviewOptions.map((option) => (
        <Box
          key={option.id}
          className={classes.interviewBox}
          onClick={option.onClick}
        >
          <Box display="flex" gap="16px">
            <img
              src={option.imageSrc}
              alt={option.id}
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '16px',
                border: '1px solid #CCD4DE',
              }}
              width={56}
            />
            <Box display="flex" flexDirection="column" gap="4px">
              <Typography variant="h6-semiBold" color="primary.main">
                {option.title}
              </Typography>
              <Typography variant="body02-bold">{option.description}</Typography>
            </Box>
          </Box>
          <ArrowBack
            className={classes.hoverButton}
            sx={{ color: '#1961BF', transform: 'rotate(180deg)' }}
          />
        </Box>
      ))}
    </Box>
  );
};

const SelectCompany = ({ onNext, setCompany = () => {}}) => {
  const classes = useStyles();
  const service = useServices();
  const snackbar = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [companiesData, setCompaniesData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const result = await service.callService(getCompaniesWithDetails, {});
        const companiesArray = Object.values(result); 
        setCompaniesData(companiesArray);
      } catch (error) {
        console.error(error);
        snackbar.error("Uh Oh! Unable to get the companies details.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);  

  const filteredCompanies = companiesData?.filter((company) =>
    company.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelection = (company) => {
    setCompany(company)
    onNext();
  }

  return (
    <Box className={classes.selectCompany}>
      <SearchTextField
        placeholder="Search for company"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      <Box className={classes.companies}>
        {loading ? (
          // Skeleton placeholders (2 cards)
          [1, 2 , 3 , 4 , 5 , 6].map((_, index) => (
            <Box key={index} className={classes.companyCard}>
              <Skeleton variant="rectangular" width={56} height={56} style={{ borderRadius: 16 }} />
              <Box sx={{ ml: 2, flex: 1 }}>
                <Skeleton width="80%" height={24} />
              </Box>
            </Box>
          ))
        ) : (
          filteredCompanies.map((company, index) => (
            <Box key={company._id} className={classes.companyCard} onClick={() => handleSelection(company)}>
             <Box className={classes.cardImgContainer}>
              <img
                src={`https://assets.languify.in/images/${company.name.replace(/\s+/g, '_')}.png`}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://assets.languify.in/images/companyTag.png";
                }}
                width={56}
                alt="logo"
                sx={{backgroundColor : '#FFFFFF'}}
              />
            </Box>
              <Typography
                variant="h6-semiBold"
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "calc(100% - 72px)",
                  marginLeft: 16,
                }}
              >
                {company.name.charAt(0).toUpperCase() + company.name.slice(1)}
              </Typography>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

const MockDetails = ({
  onNext,
  company,
  initialFilters = null,
  setSelectedChallenge,
  selectedChallenge,
  setSelectedTopic,
  selectedTopic,
  templateType
}) => {
  const classes = useStyles();
  const service = useServices();
  const snackbar = useSnackbar();

  const [autoAssign, setAutoAssign] = useState(true);
  const [loading, setLoading] = useState(false);
  const [topicsData, setTopicsData] = useState({});
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);

  const [challengeOptions, setChallengeOptions] = useState([]);

  const fetchTopicsData = async () => {
    try {
      setLoading(true);
      const result = await service.callService(getSubTopicsWithDetails, {
        company: company?._id,
        ...initialFilters,
      });
      setTopicsData(result);
    } catch (error) {
      console.error(error);
      showError("Uh Oh! Unable to get the topics details.");
    } finally {
      setLoading(false);
    }
  };

  const fetchChallenges = async () => {
    try {
      setLoading(true);
      const filters = buildFilters(true);
      const result = await service.callService(getChallengesByTags, filters);

      const challengesForDifficulty = result?.[selectedDifficulty] || [];
      const options = transformChallengesForDropdown(challengesForDifficulty);

      setChallengeOptions(options);

      if (autoAssign && options.length > 0 && (templateType === TemplateType.MOCK)) {
        const randomChallenge = selectRandomChallenge(options);
        setSelectedChallenge(randomChallenge);
      }
    } catch (error) {
      console.log(error);
      showError("Uh Oh! Unable to get the challenges.");
    } finally {
      setLoading(false);
    }
  };

  const buildFilters = (includeTopic = false) => {
    const filters = {
      ...initialFilters,
      tags: [],
      includeCompanyTags: company?._id ? true : false,
    };

    if (company?._id) filters.tags.push(company._id);
    if (includeTopic && selectedTopic?._id) filters.tags.push(selectedTopic._id);

    return filters;
  };

  const transformChallengesForDropdown = (challenges) => {
    return challenges.map((challenge) => ({
      ...challenge,
      name: challenge.title,
      _id: challenge._id,
    }));
  };

  const selectRandomChallenge = (options) => {
    if (options.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * options.length);
    return options[randomIndex];
  };

  const formatDifficultyName = (difficulty) => {
    return difficulty
      .replace("_", " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const showError = (message) => {
    snackbar.error(message);
  };

  const topicOptions = useMemo(() => {
    return Object.values(topicsData).map((topic) => ({
      ...topic,
      name: topic.name.charAt(0).toUpperCase() + topic.name.slice(1),
    }));
  }, [topicsData]);
  

  const difficultyOptions = useMemo(() => {
    if (!selectedTopic || !topicsData) return [];

    const fullTopic = Object.values(topicsData).find(
      (t) => t._id === selectedTopic._id
    );

    if (!fullTopic) return [];

    const difficulties = ["very_easy", "easy", "medium", "hard"];

    return difficulties
      .filter((diff) => {
        const value = fullTopic[diff];
        return Array.isArray(value) && value[0] < value[1];
      })
      .map((diff) => ({
        _id: diff,
        name: formatDifficultyName(diff),
      }));
  }, [selectedTopic, topicsData]);

  const canShowChallengeSelect =
  (templateType !== TemplateType.MOCK || !autoAssign) && !loading;


  const handleTopicChange = useCallback(
    (e) => {
      const topicId = e.target.value;
      const topicObj = topicOptions.find((t) => t._id === topicId);
      setSelectedTopic(topicObj || null);
      setSelectedDifficulty(null);
      setSelectedChallenge(null);
      setChallengeOptions([]);
    },
    [topicOptions]
  );

  const handleDifficultyChange = useCallback((e) => {
    setSelectedDifficulty(e.target.value);
    setSelectedChallenge(null);
  }, []);

  const handleChallengeChange = useCallback(
    (e) => {
      const challengeId = e.target.value;
      const challengeObj = challengeOptions.find((ch) => ch._id === challengeId);
      setSelectedChallenge(challengeObj || null);
    },
    [challengeOptions]
  );

  const handleCheckboxChange = useCallback(
    (event) => {
      const isChecked = event.target.checked;
      setAutoAssign(isChecked);

      if (isChecked && challengeOptions.length > 0) {
        const randomChallenge = selectRandomChallenge(challengeOptions);
        setSelectedChallenge(randomChallenge);
      } else if (!isChecked) {
        setSelectedChallenge(null);
      }
    },
    [challengeOptions]
  );

  useEffect(() => {
    fetchTopicsData();
  }, [company]);

  useEffect(() => {
    if (!selectedTopic || !selectedDifficulty) {
      setChallengeOptions([]);
      setSelectedChallenge(null);
      return;
    }

    fetchChallenges();
  }, [selectedTopic, selectedDifficulty, company, initialFilters]);

  const renderLoadingSkeletons = () => (
    <>
      <Skeleton height={48} width="100%" sx={{ borderRadius: 1, mb: 2 }} />
      <Skeleton height={48} width="100%" sx={{ borderRadius: 1, mb: 2 }} />
    </>
  );

  const renderSelectionControls = () => (
    <>
      <SimpleSelect
        label="DSA topic"
        value={selectedTopic?._id || ""}
        onChange={handleTopicChange}
        options={topicOptions}
      />
      <SimpleSelect
        label="Difficulty"
        value={selectedDifficulty}
        onChange={handleDifficultyChange}
        options={difficultyOptions}
      />
    </>
  );

  const renderChallengeSelect = () => {
    if (!canShowChallengeSelect) return null;

    const handleChallengeOpen = () => {
      if (!selectedDifficulty) {
        snackbar.error("Please select difficulty first");
      }
    };

    return (
      <SimpleSelect
        label="Challenge"
        value={selectedChallenge?._id || ""}
        onChange={handleChallengeChange}
        options={challengeOptions}
        onOpen={handleChallengeOpen}
      />
    );
  };

  const renderAutoAssignCheckbox = () => (
    <Box display="flex" alignItems="center" gap="16px">
      <Checkbox
        checked={autoAssign}
        onChange={handleCheckboxChange}
        sx={{
          padding: 0,
          color: "#003E8C",
          "&.Mui-checked": {
            color: "#003E8C",
          },
        }}
      />
      <Typography variant="body1">
        Automatically assign a coding challenge based on your selection.
      </Typography>
    </Box>
  );

  return (
    <Box className={classes.selectCompany}>
      {loading ? renderLoadingSkeletons() : renderSelectionControls()}
      {renderChallengeSelect()}
      {templateType === TemplateType.MOCK && renderAutoAssignCheckbox()}

      <CustomButton
        size="medium"
        onClick={onNext}
        disabled={!selectedTopic || !selectedDifficulty}
      >
        Proceed
      </CustomButton>
    </Box>
  );
};

  export const Proctorship = ({ onFinish ,templateType , initialValues , editPage = false }) => {
    const classes = useStyles();
    const [options, setOptions] = React.useState([]);

    React.useEffect(() => {
      getBatches({ client: Session.userId })
        .then(({ batches }) => setOptions(batches))
        .catch(console.error);
    }, []);
    
    const validationSchema = Yup.object().shape({
      name: Yup.string().required('Mock name is required'),
      batch: Yup.string().required('Please select a batch'),
      publishOn: Yup.date().required('Publish date is required'),
      expiresOn: Yup.date()
        .required('Expiry date is required')
        .min(
          Yup.ref('publishOn'),
          'Expiry date must be after publish date'
        ),
        maxAllowedAttempts: Yup.number()
        .typeError('Must be a number')
        .required('Number of attempts is required')
        .min(1, 'Minimum 1 attempt allowed')
        .integer('Only whole numbers are allowed'),
       proctorSettings: Yup.object({
          copyPasteProctoring: Yup.bool().optional().default(true),
          generalProctoring: Yup.bool().optional().default(false),
          imageCaptureProctoring: Yup.bool().optional().default(false),
        }).optional(),
    });
  
    const formik = useFormik({
      initialValues,
      validationSchema,
      onSubmit: (values) => {    
        onFinish && onFinish(values);
      },
    });

    const handleDateChange = React.useCallback((name, date) => {
      formik.setFieldValue(name, date);
    }, [formik]);
    
  
    const { batch, expiresOn, name, publishOn } = formik.values;
    const isPublishDisabled = !formik.isValid || !batch || !expiresOn || !name || !publishOn;
    

    return (
        <form className={classes.selectCompany} onSubmit={formik.handleSubmit}>
          <TextField
            label="Coding Mock name"
            name="name"
            placeholder="Enter mock name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            sx={{ width: '100%' }}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />
  
          <SimpleSelect
            label="Publishing in Batch"
            name="batch"
            options={options}
            value={formik.values.batch}
            sx={{ width: '100%' }}
            onChange={formik.handleChange}
            error={formik.touched.batch && Boolean(formik.errors.batch)}
            helperText={formik.touched.batch && formik.errors.batch}
          />

          <TextField
            label={
              <Box display='flex' alignItems='center' gap='4px'>
                Number of attempts user can take
                {templateType === TemplateType.CONTEST && <InfoIconWithTooltip 
                  title='In contest student can take only one attempt.' 
                  placement="top" 
                />}
              </Box>
            }
            name="maxAllowedAttempts"
            placeholder="Enter number of attempts"
            type="number"
            value={formik.values.maxAllowedAttempts}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            sx={{ width: '100%' }}
            inputProps={{ min: 1 }}
            error={formik.touched.maxAllowedAttempts && Boolean(formik.errors.maxAllowedAttempts)}
            helperText={formik.touched.maxAllowedAttempts && formik.errors.maxAllowedAttempts}
            disabled={templateType === TemplateType.CONTEST ? true : false}
          />

          {templateType === TemplateType.MOCK ? (
            <MockDatePicker
              name="publishOn"
              label="Publish on"
              value={formik.values.publishOn}
              onChange={handleDateChange}
              disabled={false}
            />
          ) : (
            <SelectDatePicker
              name="publishOn"
              label="Publish on"
              value={formik.values.publishOn}
              onChange={handleDateChange}
              disabled={false}
            />
          )}

          {templateType === TemplateType.MOCK ? (
            <MockDatePicker
              name="expiresOn"
              label="Expires on"
              value={formik.values.expiresOn}
              onChange={handleDateChange}
              disabled={!formik.values.publishOn}
              minDate={formik.values.publishOn}
            />
          ) : (
            <SelectDatePicker
              name="expiresOn"
              label="Expires on"
              value={formik.values.expiresOn}
              onChange={handleDateChange}
              disabled={!formik.values.publishOn}
              minDate={formik.values.publishOn}
            />
          )}
  
         <ProctorSettings
            values={formik?.values?.proctorSettings || {}}
            onChange={formik.handleChange}
            boxShadow='none'
          />
  
          <Box sx={{ marginTop: '20px' , width : '100%' }}>           
            <CustomButton
              variant="contained"
              type="submit"
              sx={{width : '100%'}}
              disabled={isPublishDisabled}
            >
              {editPage ? 'Save' : 'Publish Now'}
            </CustomButton>
          </Box>
        </form>
    );
  };

const CreateCodingInterview = ({setRefresh , open , onClose , currentStep , setCurrentStep , 
  setInterviewType , interviewType , templateType , setTemplateType}) => {
  const classes = useStyles();
  const service = useServices();
  const snackbar = useSnackbar();

  const [company , setCompany] = useState(null)
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  
  const getSteps = () => {
    if (interviewType === INTERVIEW_TYPE.COMPANY) {
      return [
        MockPublishingSteps.CHOOSE_COMPANY,
        MockPublishingSteps.MOCK_DETAILS,
        MockPublishingSteps.PUBLISHING_PROCTORING
      ];
    } else if (interviewType === INTERVIEW_TYPE.DSA || interviewType === INTERVIEW_TYPE.CONTEST) {
      return [
        MockPublishingSteps.MOCK_DETAILS,
        MockPublishingSteps.PUBLISHING_PROCTORING
      ];
    }
    return [];
  };
  
  const steps = getSteps();
  const currentStepIndex = currentStep ? steps.indexOf(currentStep) : -1;

  const handleBack = () => {
    
    if (currentStep === null) {
      return;
    }

    if(currentStep === MockPublishingSteps.MOCK_DETAILS && interviewType === TemplateType.CONTEST){
      // setCurrentStep(null)
      setInterviewType(null);
      onClose();
    }
    
    const prevStepIndex = currentStepIndex - 1;
    if (prevStepIndex >= 0) {
      setCurrentStep(steps[prevStepIndex]);
    } else {
      setCurrentStep(null);
      setInterviewType(null);
    }
  };

  const handleNext = () => {
    const nextStepIndex = currentStepIndex + 1;
    if (nextStepIndex < steps.length) {
      setCurrentStep(steps[nextStepIndex]);
    }
  };

  const handleSelectDSA = () => {
    setInterviewType('dsa');
    setCurrentStep(MockPublishingSteps.MOCK_DETAILS);
    setCompany(null)
    setTemplateType(TemplateType.MOCK)
  };

  const handleSelectCompany = () => {
    setInterviewType('company');
    setCurrentStep(MockPublishingSteps.CHOOSE_COMPANY);
    setTemplateType(TemplateType.MOCK)
  };

  const handleContest = () => {
    setInterviewType('company');
    setCurrentStep(MockPublishingSteps.MOCK_DETAILS);
    setCompany(null)
    setTemplateType(TemplateType.CONTEST)
  }

  const handleFinish = async (values) => {
    try {
      const result = await service.callService(createChallenge, {
        type: templateType,
        batch: values.batch,
        user: Session.userId,
        details: {
          subTopics: [selectedTopic?._id],
          challenges: [selectedChallenge?._id],
          company: company?._id,
        },
        settings: {
          name: values.name,
          publishOn: values.publishOn,
          expiresOn: values.expiresOn,
          maxAllowedAttempts: values.maxAllowedAttempts,
          proctorSettings: values.proctorSettings,
        },
      });
  
      setCurrentStep(null);
      setInterviewType(null);
  
      if (result) {
        setRefresh(true);
        snackbar.success('Challenge created successfully!');
        onClose();
      }
    } catch (error) {
      snackbar.error(error?.message || 'Failed to create challenge. Please try again.');
    }
  };
  

  const renderCurrentStep = () => {
    if (!currentStep) {
      return (
        <InterviewSelection
          onSelectDSA={handleSelectDSA}
          onSelectCompany={handleSelectCompany}
        />
      );
    }

    const initialValues = {
      name: `${selectedTopic?.name?.charAt(0).toUpperCase() + selectedTopic?.name?.slice(1) || ""} : ${selectedChallenge?.name || ""}`,
      batch: '',
      publishOn: '',
      expiresOn: '',
      maxAllowedAttempts: '1', 
      proctorSettings: {
        copyPasteProctoring: true,
        generalProctoring: true,
        imageCaptureProctoring: false,
      },
    };

    switch (currentStep) {
      case MockPublishingSteps.CHOOSE_COMPANY:
        return <SelectCompany onNext={handleNext} setCompany={setCompany}/>;
      case MockPublishingSteps.MOCK_DETAILS:
        return <MockDetails 
                  onNext={handleNext} 
                  company={company}
                  setSelectedChallenge={setSelectedChallenge}
                  selectedChallenge={selectedChallenge}
                  setSelectedTopic={setSelectedTopic}
                  selectedTopic={selectedTopic}
                  templateType={templateType}
                />;
      case MockPublishingSteps.PUBLISHING_PROCTORING:
        return <Proctorship 
                  onFinish={handleFinish} 
                  templateType={templateType}
                  initialValues={initialValues}
                />;
      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}  
      maxWidth='700px' 
      width='100%'  
      scroll="paper"
      sx={{
        '& .MuiBackdrop-root': {
           backgroundColor: 'rgba(0, 17, 38, 0.7)',
           backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
        },
      }}
    >
       <IconButton
            onClick={onClose}
            sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 1,
                backgroundColor: 'white',
                '&:hover': {
                backgroundColor: '#f0f0f0',
                }
            }}
        >
            <CloseIcon />
        </IconButton>
      <Box display='flex' flexDirection='column'>
        <Box className={classes.container}>
          <Box className={classes.box}>
            <Header onBack={handleBack} currentStep={currentStep} />
            {renderCurrentStep()}
          </Box>
        </Box>   
      </Box>
    </Dialog>
  );
};

export default CreateCodingInterview;